import { serviceAreaFor, serviceAreaIndex } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useTRPC } from '../../../lib/trpc.js'

/**
 * Which service area a booking's suburb is in, for Colour by area (ADR 021).
 *
 * The areas are not on the job — they are a list of suburbs and postcodes in
 * Settings — so the board fetches that list once and matches each address
 * through `serviceAreaFor`, the same helper the solver uses. It is fetched
 * only while the board is coloured by area or showing the map, whose Area
 * filter and grouping read it too.
 *
 * By context rather than a prop, like the search term, so the four boards
 * between the toolbar and a block do not carry a lookup they never use.
 */

export interface BoardArea {
  id: string
  name: string
  colour: string
}

type AreaAddress = { suburb?: string | null; city?: string | null; postcode?: string | null }

export type AreaOf = (address: AreaAddress | null | undefined) => BoardArea | null

const NO_AREAS: AreaOf = () => null

interface BoardAreas {
  areaOf: AreaOf
  /** False until the list has loaded and has at least one area in it. */
  any: boolean
  /** In Settings order, for the map's Area filter. */
  areas: BoardArea[]
}

const BoardAreasContext = createContext<BoardAreas>({ areaOf: NO_AREAS, any: false, areas: [] })

export function BoardAreasProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const trpc = useTRPC()
  const list = useQuery({ ...trpc.serviceAreas.list.queryOptions(), enabled })

  const value = useMemo<BoardAreas>(() => {
    const areas = list.data ?? []
    if (!enabled || areas.length === 0) return { areaOf: NO_AREAS, any: false, areas: [] }

    const listed = areas.map((area) => ({ id: area.id, name: area.name, colour: area.colour }))
    const byId = new Map(listed.map((area) => [area.id, area]))
    const index = serviceAreaIndex(
      areas.flatMap((area) => area.places.map((place) => ({ ...place, areaId: area.id }))),
    )
    return {
      any: true,
      areas: listed,
      areaOf: (address) => {
        const id = serviceAreaFor(address, index)
        return id ? (byId.get(id) ?? null) : null
      },
    }
  }, [enabled, list.data])

  return <BoardAreasContext.Provider value={value}>{children}</BoardAreasContext.Provider>
}

export function useAreaOf(): AreaOf {
  return useContext(BoardAreasContext).areaOf
}

/** Whether any service area exists, for saying so when colouring by area shows nothing. */
export function useHasAreas(): boolean {
  return useContext(BoardAreasContext).any
}

export function useBoardAreas(): BoardArea[] {
  return useContext(BoardAreasContext).areas
}
